/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-06-12 14:07:47
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { SubmissionError } from 'redux-form';
import { store } from 'src/stores/store';
import { FAKED_PRODUCT_COUNT } from 'src/constants/filter-tree';

class MetafieldHelper {
  /**
   * Init values from first loading
   *
   * @param  {object} filterOption  filter option value
   * @param  {object} initialValue default values
   *
   * @return {object}               init values
   */
  static initialValues = (filterOption: Object, initialValue: Object) => {
    const {
      displayType,
      swatchStyle = 'circle-grid',
      manualValues,
      valueType,
      selectType,
      excludedValues
    } = filterOption;

    let metafieldManualValues = [];
    let metafieldExcludedValues = [];

    if (valueType === 'specific') {
      metafieldManualValues = manualValues
        ? manualValues.map(item => ({
            value: item,
            label: item
          }))
        : [];
    } else {
      metafieldExcludedValues = excludedValues
        ? excludedValues.map(item => ({
            value: item,
            label: item
          }))
        : [];
    }

    const result = {
      ...initialValue,
      metafield_DisplayType: displayType,
      metafield_SwatchStyle: swatchStyle,
      metafield_SelectType: selectType,
      metafield_ValueType: valueType,
      metafield_ManualValues: metafieldManualValues,
      metafield_ExcludedValues: metafieldExcludedValues
    };

    return result;
  };

  /**
   * Build filter metafield data based on input values
   *
   * @param  {object} filterOption    input values
   * @param  {object} defaultValues   default values
   *
   * @return {object}                 filter metafield values
   */
  static buildFilterOptionValues = (filterOption: Object, defaultValues: Object) => {
    let {
      metafield_DisplayType: displayType,
      metafield_SwatchStyle: swatchStyle,
      metafield_SelectType: selectType,
      metafield_ValueType: valueType,
      metafield_ManualValues: metafieldManualValues,
      metafield_ExcludedValues: metafieldExcludedValues
    } = filterOption;

    let manualValues = null;
    let excludedValues = null;
    if (valueType === 'specific') {
      manualValues = metafieldManualValues ? metafieldManualValues.map(item => item.value) : null;
    } else {
      excludedValues = metafieldExcludedValues
        ? metafieldExcludedValues.map(item => item.value)
        : null;
    }

    const result = {
      ...defaultValues,
      displayType,
      swatchStyle,
      selectType,
      valueType,
      manualValues,
      removeTextFilterValues: '',
      replaceTextFilterValues: null,
      excludedValues
    };
    return result;
  };

  /**
   * Validate for option
   *
   * @param  {object} filterOption
   * @param  {I18n} i18n
   *
   * @return {boolean/error} void if well validated, throw error if have
   */
  static validateFilterOption = (filterOption: Object, i18n: Object) => {
    // Check manual value
    const { metafield_ValueType } = filterOption;
    if (metafield_ValueType === 'specific') {
      if (_.isEmpty(filterOption.metafield_ManualValues)) {
        throw new SubmissionError({
          metafield_ManualValues: i18n.translate(
            'AdminHTML.FilterTree.Helper.helper_ValidateFilterOption_SubmissionError1'
          )
        });
      }
    }
  };

  /**
   * Fake product data for option
   *
   * @param  {object} filterOption
   * @return {array}              list of product count
   */
  static fakeProductValues = (filterOption: Object) => {
    const { valueType, filterType, excludedValues } = filterOption;
    let values = [];

    if (valueType === 'specific') {
      // Limit product item
      let manualValues = _.get(filterOption, 'manualValues', []);
      manualValues = _.slice(manualValues, 0, FAKED_PRODUCT_COUNT);

      values = _.map(manualValues, item => {
        return {
          doc_count: Math.floor(Math.random() * 100) + 1,
          key: item
        };
      });
    } else {
      // Get metafield values
      const currentState = store.getState();
      const field = filterType.replace('metafield_::', 'metafield::');
      let options = _.cloneDeep(_.get(currentState.filterData, `filterData.${field}`, []));

      if (!_.isEmpty(excludedValues)) {
        _.map(excludedValues, item => _.remove(options, i => i.value === item));
      }

      // Limit product item
      const subOptions = _.slice(options, 0, FAKED_PRODUCT_COUNT);

      values = _.map(subOptions, item => {
        return {
          doc_count: Math.floor(Math.random() * 100) + 1,
          key: item.value
        };
      });
    }

    return values;
  };
}

export default MetafieldHelper;
